import React, { useState, useEffect } from 'react';
import { recommendationsApi, Recommendation, RecommendationFilters } from '../../services/recommendations';
import { RecommendationCard } from './RecommendationCard';
import { RecommendationFilters as FiltersComponent } from './RecommendationFilters';

export const RecommendationsList: React.FC = () => {
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<RecommendationFilters>({});
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchRecommendations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await recommendationsApi.getRecommendations(page, 20, filters);
      
      // Ensure we always have an array
      const recommendationsData = Array.isArray(response?.recommendations) 
        ? response.recommendations 
        : [];
        
      setRecommendations(recommendationsData);
      setTotal(response?.total || recommendationsData.length || 0);
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
      setError('Failed to load recommendations. Please try again.');
      setRecommendations([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecommendations();
  }, [page, filters]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">AI Recommendations</h1>
        <div className="text-sm text-gray-600">
          {total} total recommendations
        </div>
      </div>

      <FiltersComponent
        filters={filters}
        onFiltersChange={setFilters}
        onClear={() => setFilters({})}
      />

      {loading ? (
        <div className="text-center py-8">Loading recommendations...</div>
      ) : error ? (
        <div className="text-center py-8 text-red-600">
          {error}
          <button 
            onClick={fetchRecommendations}
            className="block mx-auto mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      ) : recommendations.length === 0 ? (
        <div className="text-center py-8 text-gray-600">
          No recommendations found.
        </div>
      ) : (
        <div className="space-y-4">
          <div className="text-sm text-gray-600 mb-4">
            Debug: Found {recommendations.length} recommendations
          </div>
          {Array.isArray(recommendations) && recommendations.map((recommendation, index) => (
            <div key={recommendation.id} className="border p-2 mb-2">
              <div className="text-sm text-gray-600">Recommendation #{index + 1}</div>
              <RecommendationCard 
                recommendation={recommendation}
              />
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      <div className="flex justify-center space-x-2">
        <button
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1}
          className="px-4 py-2 border rounded disabled:opacity-50"
        >
          Previous
        </button>
        <span className="px-4 py-2">
          Page {page} of {Math.ceil(total / 20)}
        </span>
        <button
          onClick={() => setPage(p => p + 1)}
          disabled={page >= Math.ceil(total / 20)}
          className="px-4 py-2 border rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};